import {
  Loading,
  NotAuthorized,
  NotFound,
  PageContent,
  PageHeader,
} from '@/components';
import { usePriorIdFromLineage } from '@/features/project/_hooks/project';
import { useProject } from '@/features/project/_hooks/project';
import {
  useChangeBrowserTabTitleEffect,
  useElementOffsetHeight,
  useId,
} from '@/hooks';
import {
  HEADER_ID_FOR_OFFSET_HEIGHT,
  PAGE_HEADER_PADDING,
  PS_HEADER_ID_FOR_OFFSET_HEIGHT,
  isPS,
} from '@/utils';
import type { AxiosError } from 'axios';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectsPageDynamicLink } from '../../../_components/ProjectsPageDynamicLink';
import { ReviewSheetModel } from '../../../_models/project';
import { ProjectAndDiffSwitcher } from '../../_components/ProjectAndDiffSwitcher';
import { ProjectNameTitle } from '../../_components/ProjectNameTitle';
import { RsProjectDiffView } from '../_components/RsProjectDiffView';

export const ProjectDiffPage: FC = () => {
  const { t } = useTranslation();
  const { id: sourceId } = useId();
  const sourceProject = useProject(sourceId);

  const priorId = usePriorIdFromLineage({
    params: {
      fiscalYear: sourceProject.data?.fiscalYear,
      lineageId: sourceProject.data?.lineageId ?? '',
    },
  });

  const targetId = priorId.data?.priorProjectId ?? '';
  const targetProject = useProject(targetId);
  useChangeBrowserTabTitleEffect({ title: sourceProject.data?.name });

  const headerHeight = useElementOffsetHeight({
    targetId: isPS()
      ? PS_HEADER_ID_FOR_OFFSET_HEIGHT
      : HEADER_ID_FOR_OFFSET_HEIGHT,
  });

  if (sourceProject.isError) {
    if ((sourceProject.error as AxiosError).response?.status === 403) {
      return (
        <NotAuthorized
          message={t('error-message-not-project-authorized')}
          height={`calc(100vh - ${headerHeight}px - ${PAGE_HEADER_PADDING * 2}px)`}
          backRoute="/rs/project"
        />
      );
    }
    return <NotFound />;
  }

  if (sourceProject.isLoading || targetProject.isLoading) {
    return <Loading />;
  }

  return (
    <>
      <PageHeader
        title={<ProjectNameTitle />}
        breadcrumbs={[
          { title: <ProjectsPageDynamicLink /> },
          {
            title: t('project-details-page'),
            href: `${location.pathname.split('/').slice(0, -1).join('/')}`,
          },
          {
            title: t('project-diff-page'),
          },
        ]}
        buttons={<ProjectAndDiffSwitcher />}
      />
      <PageContent>
        <RsProjectDiffView
          sourceProject={ReviewSheetModel.parse(sourceProject.data)}
          targetProject={ReviewSheetModel.parse(targetProject.data)}
        />
      </PageContent>
    </>
  );
};
